import type { BingoBoardState, BoardChange, StateReason } from "../shared/types";
import { element } from "./dom";

type BoardViewOptions = {
  compact?: boolean;
  showNumbers?: boolean;
};

type CellElements = {
  cell: HTMLDivElement;
  text: HTMLDivElement;
  fontScale: number;
};

const FONT_FAMILY_CSS = {
  sans: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  rounded: '"Trebuchet MS", "Arial Rounded MT Bold", Arial, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", serif',
  mono: 'Consolas, "Courier New", monospace',
} as const;

export class BingoBoardView {
  readonly element: HTMLDivElement;
  private readonly cells: CellElements[];
  private readonly resizeObserver: ResizeObserver;

  constructor(options: BoardViewOptions = {}) {
    this.element = element("div", {
      className: `bingo-board${options.compact ? " bingo-board--compact" : ""}`,
      attrs: { "aria-label": "Bingokaart" },
    });
    this.cells = Array.from({ length: 9 }, (_, index) => {
      const text = element("div", { className: "bingo-cell__text" });
      const shade = element("div", { className: "bingo-cell__shade", attrs: { "aria-hidden": "true" } });
      const lineOne = element("span", { className: "bingo-cell__cross-line bingo-cell__cross-line--one" });
      const lineTwo = element("span", { className: "bingo-cell__cross-line bingo-cell__cross-line--two" });
      const cross = element("div", {
        className: "bingo-cell__cross",
        attrs: { "aria-hidden": "true" },
        children: [lineOne, lineTwo],
      });
      const number = options.showNumbers
        ? element("span", { className: "bingo-cell__number", text: String(index + 1), attrs: { "aria-hidden": "true" } })
        : null;
      const cell = element("div", {
        className: "bingo-cell",
        attrs: { "data-cell-id": String(index) },
        children: [shade, text, cross, number],
      });
      this.element.append(cell);
      return { cell, text, fontScale: 100 };
    });

    this.resizeObserver = new ResizeObserver(() => this.fitAllText());
    this.resizeObserver.observe(this.element);
  }

  render(state: BingoBoardState, reason: StateReason, change?: BoardChange): void {
    for (const model of state.cells) {
      const view = this.cells[model.id];
      if (!view) continue;
      if (view.text.textContent !== model.text) view.text.textContent = model.text;
      view.fontScale = model.fontScale;
      view.text.style.fontFamily = FONT_FAMILY_CSS[model.fontFamily];
      view.cell.setAttribute("aria-label", `Vak ${model.id + 1}: ${model.text || "leeg"}${model.completed ? ", voltooid" : ""}`);

      const wasCompleted = view.cell.classList.contains("is-completed");
      view.cell.classList.toggle("is-completed", model.completed);
      view.cell.classList.remove("is-completing");

      const shouldAnimate =
        reason === "update" &&
        change?.type === "completion" &&
        change.cellId === model.id &&
        change.completed &&
        !wasCompleted;
      if (shouldAnimate) {
        // Restarting the class in a separate frame reliably replays the CSS animation in OBS Chromium.
        requestAnimationFrame(() => view.cell.classList.add("is-completing"));
      }
    }
    requestAnimationFrame(() => this.fitAllText());
  }

  destroy(): void {
    this.resizeObserver.disconnect();
  }

  private fitAllText(): void {
    for (const { cell, text, fontScale } of this.cells) this.fitText(cell, text, fontScale);
  }

  private fitText(cell: HTMLElement, text: HTMLElement, fontScale: number): void {
    if (!text.textContent) {
      text.style.fontSize = "";
      return;
    }

    const minimum = Math.max(8, Math.min(16, cell.clientWidth * 0.065));
    const automaticMaximum = Math.max(12, Math.min(58, cell.clientWidth * 0.19));
    const maximum = Math.max(minimum, automaticMaximum * (fontScale / 100));
    let low = minimum;
    let high = maximum;
    text.style.fontSize = `${low}px`;

    for (let iteration = 0; iteration < 9; iteration += 1) {
      const middle = (low + high) / 2;
      text.style.fontSize = `${middle}px`;
      const fits = text.scrollHeight <= text.clientHeight + 1 && text.scrollWidth <= text.clientWidth + 1;
      if (fits) low = middle;
      else high = middle;
    }
    text.style.fontSize = `${Math.floor(low * 10) / 10}px`;
  }
}
